import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { LiveRates } from '@/lib/priceCalculator';

const STORAGE_KEY = 'aurum_pinned_snapshot_v1';
const EVENT = 'aurum:pinned-snapshot-changed';

export interface PinnedSnapshot {
  /** Timestamp of the original history entry that was pinned. */
  pinnedFromTs: number;
  /** When the user pinned it. */
  pinnedAt: number;
  /** Frozen rates used for all calculations while pinned. */
  rates: LiveRates;
  /** Original source label of the entry (live/cache/fallback). */
  originSource: string;
}

interface Ctx {
  pinned: PinnedSnapshot | null;
  pin: (entry: { ts: number; source: string; rates: LiveRates }) => void;
  unpin: () => void;
  isPinned: (ts: number) => boolean;
}

const PinnedContext = createContext<Ctx | null>(null);

function load(): PinnedSnapshot | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function save(s: PinnedSnapshot | null) {
  try {
    if (s) localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    else localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(EVENT, { detail: s }));
  } catch { /* noop */ }
}

export function PinnedSnapshotProvider({ children }: { children: ReactNode }) {
  const [pinned, setPinned] = useState<PinnedSnapshot | null>(null);

  useEffect(() => {
    setPinned(load());
    const onChange = (e: Event) => {
      const v = (e as CustomEvent<PinnedSnapshot | null>).detail;
      setPinned(v ?? load());
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setPinned(load());
    };
    window.addEventListener(EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const pin = useCallback((entry: { ts: number; source: string; rates: LiveRates }) => {
    const snap: PinnedSnapshot = {
      pinnedFromTs: entry.ts,
      pinnedAt: Date.now(),
      // Defensive deep clone so future mutations of history don't bleed in.
      rates: JSON.parse(JSON.stringify(entry.rates)),
      originSource: entry.source,
    };
    save(snap);
    setPinned(snap);
  }, []);

  const unpin = useCallback(() => {
    save(null);
    setPinned(null);
  }, []);

  const isPinned = useCallback((ts: number) => pinned?.pinnedFromTs === ts, [pinned]);

  const value = useMemo<Ctx>(() => ({ pinned, pin, unpin, isPinned }), [pinned, pin, unpin, isPinned]);
  return <PinnedContext.Provider value={value}>{children}</PinnedContext.Provider>;
}

export function usePinnedSnapshot() {
  const ctx = useContext(PinnedContext);
  if (!ctx) throw new Error('usePinnedSnapshot must be used inside PinnedSnapshotProvider');
  return ctx;
}
